/**
 * Mirror of `src/utils/preReleaseUpcPending.ts` (flag `prerelease_upc_pending`,
 * MOT-651): whether a staged row is pre-release for the UPC waiver. A stored
 * lifecycle reading announced or pre-order on the UTC day counts; with none,
 * `launchDates` naming a release date still ahead does. Keep the two in step.
 */

type LifecycleStage = 'announced' | 'preorder' | 'released';

export interface PreReleaseRowLike {
  upc?: unknown;
  ean?: unknown;
  launchDate?: unknown;
  launchDates?: {
    announced?: unknown;
    global?: { date?: unknown } | null;
    us?: { date?: unknown } | null;
  } | null;
  lifecycle?: {
    stage?: unknown;
    releaseDate?: unknown;
    preorderOpensAt?: unknown;
  } | null;
  manufacturerStock?: { status?: unknown } | null;
  inventory?: { manufacturerStock?: { status?: unknown } | null } | null;
}

/** `YYYY-MM-DD` of an ISO date or timestamp, or null. */
function dayOf(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const text = value.trim();
  const dateOnly = text.match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/);
  if (dateOnly && !Number.isNaN(Date.parse(`${dateOnly[1]}T00:00:00Z`))) return dateOnly[1];
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
}

function stageAt(stored: unknown, releaseDate: unknown, preorderOpensAt: unknown, now: Date): LifecycleStage {
  const today = now.toISOString().slice(0, 10);
  const release = dayOf(releaseDate);
  const opens = dayOf(preorderOpensAt);
  if (release && today >= release) return 'released';
  if (opens && today >= opens) return 'preorder';
  if (stored === 'preorder' && !opens) return 'preorder';
  if (stored === 'released' && !release) return 'released';
  return 'announced';
}

const isStage = (v: unknown): v is LifecycleStage => v === 'announced' || v === 'preorder' || v === 'released';

export function isPreReleaseForPromotion(row: PreReleaseRowLike, now: Date = new Date()): boolean {
  const lifecycle = row.lifecycle;
  if (lifecycle && isStage(lifecycle.stage)) {
    return stageAt(lifecycle.stage, lifecycle.releaseDate, lifecycle.preorderOpensAt, now) !== 'released';
  }
  const dates = row.launchDates;
  const release = dayOf(dates?.global?.date) ?? dayOf(dates?.us?.date) ?? dayOf(row.launchDate);
  if (!release) return false;
  const stock = row.inventory?.manufacturerStock?.status ?? row.manufacturerStock?.status ?? null;
  return stageAt(stock === 'preorder' ? 'preorder' : null, release, null, now) !== 'released';
}

/** No UPC or EAN and pre-release: an admin may promote it with the flag on. */
export function qualifiesForPreReleaseUpcPending(row: PreReleaseRowLike, now: Date = new Date()): boolean {
  const hasBarcode = Boolean(String(row.upc ?? '').trim() || String(row.ean ?? '').trim());
  return !hasBarcode && isPreReleaseForPromotion(row, now);
}
